'use client'

// The maker's mood: how $PINO, the project's own token, is moving the
// town. What its price is doing to the weather, the casino's luck and his
// mood. Sam, 3 Oct: this panel is the only place the site mentions a
// token at all, and nothing here sells it or links to it. (it read $3EYES
// until 31 Oct: "switch $3EYES as the worlds mood to read from $PINO")
//
// Fetches its own maker status rather than being handed it, so it can sit in
// whichever drawer wants it without that drawer learning about the sim.

import { useEffect, useState } from 'react'
import { Card, Pill } from './Drawer'
import { kawaii } from '../../lib/ui/theme'

function simBase() {
  const ws = process.env.NEXT_PUBLIC_SIM_WS_URL || ''
  return ws.replace(/^wss:\/\//, 'https://').replace(/^ws:\/\//, 'http://').replace(/\/$/, '')
}

function SectionTitle({ children, right = null }) {
  return (
    <div className="flex items-center justify-between gap-3 mb-2.5">
      <span style={{ color: kawaii.ink, fontSize: 14, fontWeight: 800, letterSpacing: '0.01em' }}>
        {children}
      </span>
      {right}
    </div>
  )
}

function makerWords(p) {
  return {
    weather: p >= 0.35 ? 'going golden' : p >= 0.1 ? 'brightening' : p <= -0.35 ? 'greying over' : p <= -0.1 ? 'dimming' : 'level',
    casino: p >= 0.35 ? 'the house is kind' : p >= 0.1 ? 'leaning kind' : p <= -0.35 ? 'the house is mean' : p <= -0.1 ? 'leaning mean' : 'playing it straight',
    unease: p <= -0.5 ? 'uneasy out there' : p <= -0.2 ? 'a slight edge' : 'calm',
    mood: p >= 0.35 ? 'a lift he cannot name' : p <= -0.35 ? 'something weighing on him' : 'level',
  }
}

function RowDot({ color }) {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" className="shrink-0">
      <circle cx="9" cy="9" r="6" fill={color} stroke={kawaii.ink} strokeWidth="2.2" />
    </svg>
  )
}

function MakerPanel({ maker }) {
  const p = maker.pulse || 0
  const w = makerWords(p)
  const rows = [
    ['the weather', w.weather, '#f7cf6b'],
    ['casino luck', w.casino, '#f0b064'],
    ['unease', w.unease, '#c05a4e'],
    ["pino's mood", w.mood, '#8fb8e8'],
  ]
  const h1 = maker.change?.h1
  const h24 = maker.change?.h24
  const fmt = (v) => (v == null ? null : `${v > 0 ? '+' : ''}${v}%`)

  return (
    <Card>
      <SectionTitle
        right={h1 != null ? <Pill bg={p >= 0 ? '#a8d6a0' : '#eda6a0'}>{fmt(h1)}</Pill> : null}
      >
        $PINO price movement
      </SectionTitle>

      {(h1 != null || h24 != null) && (
        <p style={{ color: kawaii.inkSoft, fontSize: 13.5, fontWeight: 600, marginBottom: 11 }}>
          {fmt(h1) ? `${fmt(h1)} past hour` : ''}{fmt(h1) && fmt(h24) ? ' · ' : ''}{fmt(h24) ? `${fmt(h24)} past day` : ''}
        </p>
      )}

      {/* The pulse itself, running out from a centre notch. */}
      <div style={{ position: 'relative', height: 12, borderRadius: 999, background: kawaii.creamDeep, border: kawaii.outlineThin, overflow: 'hidden', marginBottom: 13 }}>
        <div style={{ position: 'absolute', left: '50%', top: 2, bottom: 2, width: 2, background: 'rgba(47,38,80,0.35)', zIndex: 1 }} />
        <div style={{
          position: 'absolute', top: 0, bottom: 0,
          left: p >= 0 ? '50%' : `${50 + p * 50}%`,
          width: `${Math.abs(p) * 50}%`,
          background: p >= 0 ? '#d99a2b' : '#c05a4e',
          borderRadius: 999,
          backgroundImage: 'linear-gradient(to bottom, rgba(255,255,255,0.45), rgba(255,255,255,0) 60%)',
          transition: 'all 600ms cubic-bezier(0.22, 1, 0.36, 1)',
        }} />
      </div>

      {/* Label left, reading right. Tried four across when this went wide
          and Sam preferred the rows, so the card keeps its own shape and
          the layout around it is what has to give. */}
      <div className="space-y-2">
        {rows.map(([label, value, color]) => (
          <div key={label} className="flex items-center justify-between gap-3">
            <span className="flex items-center gap-2">
              <RowDot color={color} />
              <span style={{ color: kawaii.ink, fontSize: 14, fontWeight: 700 }}>{label}</span>
            </span>
            <span style={{ color: kawaii.ink, fontSize: 14, fontWeight: 600, textAlign: 'right' }}>{value}</span>
          </div>
        ))}
      </div>

      {maker.mood && (
        <p style={{ marginTop: 12, color: '#8a5a12', fontSize: 13.5, fontStyle: 'italic', lineHeight: 1.5 }}>
          {maker.mood}
        </p>
      )}
    </Card>
  )
}

/** The panel plus its own polling. Renders nothing until the sim answers. */
export function PricePanel() {
  const [maker, setMaker] = useState(null)

  useEffect(() => {
    const base = simBase()
    if (!base) return
    const pull = () => {
      fetch(`${base}/api/maker`).then((r) => r.json()).then(setMaker).catch(() => {})
    }
    pull()
    const id = setInterval(pull, 60_000)
    return () => clearInterval(id)
  }, [])

  if (!maker?.enabled) return null
  return <MakerPanel maker={maker} />
}

export default PricePanel
